import { useEffect } from 'react';
import { useGetInitDataV2 } from '@viome/viome-base/src/usecase/Bootup/BootupQuery';
import { useGetPatientContentQuery } from '@viome/viome-base/src/usecase/PatientContext/PatientContextQuery';
import { useContextStore } from './useContextStore';

/**
 * Resolves the user's available experiences after login.
 * No inference — the backend cleanly separates the two contexts:
 * - consumer bootup from getInit **v2** (patient kits excluded)
 * - patient bootup (per org) from the **content** API
 * Both payloads are stored; the active selection decides which feeds useBootup.
 */
export const useContextHydration = () => {
  const {
    data: consumerInit,
    isLoading: isConsumerLoading,
    isError: isConsumerError,
  } = useGetInitDataV2();
  const { data: patientContentData, isLoading: isPatientLoading } =
    useGetPatientContentQuery();
  const hydrate = useContextStore((s) => s.hydrate);

  const patientContent = patientContentData ?? null;
  const orgs = patientContent?.entries ?? [];

  // v2 getInit returns consumer kits only -> presence of a kit = consumer exists.
  // Check PreRnrKitId too: a pre-RnR consumer kit has kitId=null, id in PreRnrKitId.
  const consumerExists = !!(
    consumerInit?.kitDetails?.kitId ||
    consumerInit?.kitDetails?.PreRnrKitId ||
    consumerInit?.CDkitDetails?.kitId ||
    consumerInit?.CDkitDetails?.PreRnrKitId
  );

  const isLoading = isConsumerLoading || isPatientLoading;
  const isError = isConsumerError;

  useEffect(() => {
    if (!isLoading && !isError) {
      hydrate({
        consumerInit: consumerInit ?? null,
        patientContent,
        consumerExists,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError, consumerExists, orgs.length]);

  return {
    isLoading,
    isError,
    orgs,
    consumerExists,
    needsPicker: consumerExists && orgs.length >= 1,
  };
};
